import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Tent,
  Users,
  Building2,
  Search,
  Bed,
  Moon,
  Sun,
  User,
} from 'lucide-react';
import { useCampAccommodationStore } from '../stores/campAccommodationStore';
import { CampBedModal } from '../components/campAccommodation/CampBedModal';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';

export const CampDetailsPage = () => {
  const { translations } = useLanguage();
  const { tableId } = useParams<{ tableId: string }>();
  const { tables, loading, error, fetchTables } = useCampAccommodationStore();

  // Selection state
  const [selectedTentNumber, setSelectedTentNumber] = useState<number | null>(
    null
  );
  const [selectedHallNumber, setSelectedHallNumber] = useState<number | null>(
    null
  );
  const [selectedBedIndex, setSelectedBedIndex] = useState<number | null>(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<
    'all' | 'male' | 'female'
  >('all');
  const [occupancyFilter, setOccupancyFilter] = useState<
    'all' | 'occupied' | 'available'
  >('all');

  useEffect(() => {
    if (!tables[tableId!]) {
      fetchTables();
    }
  }, [tableId, tables, fetchTables]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  const table = tables[tableId!];
  if (!table)
    return (
      <ErrorMessage
        message={
          translations?.campDetails?.notFound || 'Accommodation table not found'
        }
      />
    );

  // Calculate statistics
  const totalTents = table.tents.length;
  const totalHalls = table.tents.reduce(
    (sum, tent) => sum + tent.halls.length,
    0
  );
  const totalBeds = table.tents.reduce(
    (sum, tent) =>
      sum + tent.halls.reduce((hallSum, hall) => hallSum + hall.beds.length, 0),
    0
  );
  const occupiedBeds = table.tents.reduce(
    (sum, tent) =>
      sum +
      tent.halls.reduce((hallSum, hall) => hallSum + hall.occupiedBeds, 0),
    0
  );
  const occupancyRate = Math.round((occupiedBeds / totalBeds) * 100);

  // Filter tents based on search and filters
  const filteredTents = table.tents.filter((tent) => {
    const matchesSearch = searchTerm
      ? tent.tentNumber.toString().includes(searchTerm) ||
        tent.halls.some(
          (hall) =>
            hall.hallNumber.toString().includes(searchTerm) ||
            hall.beds.some(
              (bed) =>
                bed.pilgrimName
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase()) ||
                bed.country?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        )
      : true;

    const matchesGender =
      selectedGender === 'all'
        ? true
        : tent.halls.some((hall) => hall.gender === selectedGender);

    const matchesOccupancy =
      occupancyFilter === 'all'
        ? true
        : occupancyFilter === 'occupied'
        ? tent.occupiedCapacity > 0
        : tent.occupiedCapacity < tent.totalCapacity;

    return matchesSearch && matchesGender && matchesOccupancy;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Tent className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {table.tableName}
            </h1>
            <p className="text-gray-500">{table.campName}</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Tent className="text-primary-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campDetails?.stats?.totalTents || 'Total Tents'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-primary-600">{totalTents}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Building2 className="text-blue-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campDetails?.stats?.totalHalls || 'Total Halls'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-blue-600">{totalHalls}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-50 rounded-lg">
              <Users className="text-green-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campDetails?.stats?.occupiedBeds ||
                'Occupied Beds'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {occupiedBeds} / {totalBeds}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Users className="text-yellow-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campDetails?.stats?.occupancyRate ||
                'Occupancy Rate'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{occupancyRate}%</p>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder={
                translations?.campDetails?.filters?.searchPlaceholder ||
                'Search by tent number or pilgrim name...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-right
                       focus:ring-2 focus:ring-primary-500 transition-all"
            />
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>

          {/* Gender Filter */}
          <select
            value={selectedGender}
            onChange={(e) =>
              setSelectedGender(e.target.value as 'all' | 'male' | 'female')
            }
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.campDetails?.filters?.allGenders || 'All Genders'}
            </option>
            <option value="male">
              {translations?.campDetails?.filters?.male || 'Male'}
            </option>
            <option value="female">
              {translations?.campDetails?.filters?.female || 'Female'}
            </option>
          </select>

          {/* Occupancy Filter */}
          <select
            value={occupancyFilter}
            onChange={(e) =>
              setOccupancyFilter(
                e.target.value as 'all' | 'occupied' | 'available'
              )
            }
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">
              {translations?.campDetails?.filters?.allOccupancy || 'All'}
            </option>
            <option value="occupied">
              {translations?.campDetails?.filters?.occupied || 'Occupied'}
            </option>
            <option value="available">
              {translations?.campDetails?.filters?.available || 'Available'}
            </option>
          </select>
        </div>
      </div>

      {/* Tents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTents.map((tent) => (
          <motion.div
            key={tent.tentNumber}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm overflow-hidden"
          >
            {/* Tent Header */}
            <div className="p-4 bg-gradient-to-r from-primary-50 to-primary-100 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tent className="text-primary-500" size={24} />
                  <h3 className="font-medium text-lg">
                    {translations?.campDetails?.tentHeader || 'Tent'}{' '}
                    {tent.tentNumber}
                  </h3>
                </div>
                <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full">
                  {tent.occupiedCapacity} / {tent.totalCapacity}
                </span>
              </div>
            </div>

            {/* Halls */}
            <div className="divide-y">
              {tent.halls.map((hall) => (
                <div key={hall.hallNumber} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Building2 size={20} className="text-gray-500" />
                      <h4 className="font-medium">
                        {translations?.campDetails?.hallHeader || 'Hall'}{' '}
                        {hall.hallNumber}
                      </h4>
                    </div>
                    <span
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        hall.gender === 'male'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-pink-50 text-pink-700'
                      }`}
                    >
                      {hall.gender === 'male' ? (
                        <Moon size={14} />
                      ) : (
                        <Sun size={14} />
                      )}
                      {hall.gender === 'male'
                        ? translations?.campDetails?.filters?.male || 'Male'
                        : translations?.campDetails?.filters?.female ||
                          'Female'}
                    </span>
                  </div>

                  {/* Beds Grid */}
                  <div className="grid grid-cols-4 gap-3">
                    {hall.beds.map((bed, bedIndex) => (
                      <button
                        key={bedIndex}
                        onClick={() => {
                          setSelectedTentNumber(tent.tentNumber);
                          setSelectedHallNumber(hall.hallNumber);
                          setSelectedBedIndex(bedIndex);
                        }}
                        className={`
                          relative p-3 rounded-lg border-2 transition-all hover:shadow-md
                          ${
                            bed.pilgrimId
                              ? hall.gender === 'male'
                                ? 'border-blue-200 bg-blue-50 hover:border-blue-300'
                                : 'border-pink-200 bg-pink-50 hover:border-pink-300'
                              : 'border-gray-200 hover:border-primary-200 hover:bg-primary-50'
                          }
                        `}
                      >
                        <div className="flex flex-col items-center gap-2">
                          <Bed
                            size={24}
                            className={
                              bed.pilgrimId
                                ? hall.gender === 'male'
                                  ? 'text-blue-500'
                                  : 'text-pink-500'
                                : 'text-gray-400'
                            }
                          />
                          {bed.pilgrimId ? (
                            <>
                              <User
                                size={16}
                                className={
                                  hall.gender === 'male'
                                    ? 'text-blue-500'
                                    : 'text-pink-500'
                                }
                              />
                              <div
                                className="text-xs truncate w-full text-center"
                                title={bed.pilgrimName || ''}
                              >
                                {bed.pilgrimName}
                              </div>
                              {bed.age && (
                                <span
                                  className={`absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full text-xs text-white
                                  ${
                                    bed.age > 60
                                      ? 'bg-red-500'
                                      : bed.age > 40
                                      ? 'bg-yellow-500'
                                      : 'bg-green-500'
                                  }`}
                                >
                                  {bed.age}
                                </span>
                              )}
                            </>
                          ) : (
                            <div className="text-xs text-gray-500">
                              {translations?.campDetails?.available ||
                                'Available'}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Bed Assignment Modal */}
      {selectedTentNumber !== null &&
        selectedHallNumber !== null &&
        selectedBedIndex !== null && (
          <CampBedModal
            isOpen={true}
            onClose={() => {
              setSelectedTentNumber(null);
              setSelectedHallNumber(null);
              setSelectedBedIndex(null);
            }}
            tableId={tableId!}
            tentNumber={selectedTentNumber}
            hallNumber={selectedHallNumber}
            bedIndex={selectedBedIndex}
            gender={
              tables[tableId!].tents
                .find((t) => t.tentNumber === selectedTentNumber)
                ?.halls.find((h) => h.hallNumber === selectedHallNumber)
                ?.gender || 'male'
            }
          />
        )}
    </div>
  );
};
